import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { ProfileStage, ProposedStage, ConnectedStage, DeclinedStage } from "./FlowStages";

export const ConnectionProposalCard = ({ person, startStage, onClose }) => {
  const [stage, setStage] = useState(startStage);
  const [karna, setKarna] = useState(false);
  const [other, setOther] = useState("idle");

  const reset = (s) => {
    setStage(s);
    setKarna(false);
    setOther("idle");
  };

  useEffect(() => {
    if (person) reset(startStage);
  }, [person, startStage]);

  useEffect(() => {
    if (other === "reviewing") {
      const t = setTimeout(() => setOther("accepted"), 1500);
      return () => clearTimeout(t);
    }
    if (other === "accepted" && karna && stage === "proposed") {
      const t = setTimeout(() => setStage("connected"), 700);
      return () => clearTimeout(t);
    }
  }, [other, karna, stage]);

  const title = stage === "profile" ? person?.name : "Every new connection begins with a choice.";

  return (
    <Dialog.Root open={!!person} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="modal-overlay fixed inset-0 z-[80]" />
        {person && (
          <Dialog.Content
            data-testid="connection-flow-modal"
            data-stage={stage}
            className="glass glass-heavy modal-content fixed left-1/2 top-1/2 z-[90] max-h-[90vh] w-[min(540px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[36px] p-5 sm:p-7"
          >
            <p className="text-[12px] font-semibold text-[#7866E8]">{stage === "profile" ? "Profile preview" : `Karna ↔ ${person.name}`} · Sample content — fictional demo</p>
            <Dialog.Title className="mt-2 pr-10 text-[26px] font-extrabold leading-[1.05] tracking-[-0.035em] text-[#08090D] sm:text-[30px]" data-testid="connection-flow-title">
              {title}
            </Dialog.Title>
            <Dialog.Description className="sr-only">A mutual-consent introduction flow between Karna and {person.name}.</Dialog.Description>
            {stage === "profile" && <ProfileStage person={person} onPropose={() => reset("proposed")} />}
            {stage === "proposed" && (
              <ProposedStage
                person={person}
                karna={karna}
                other={other}
                onAccept={() => {
                  setKarna(true);
                  setOther("reviewing");
                }}
                onDecline={() => setStage("declined")}
              />
            )}
            {stage === "connected" && <ConnectedStage person={person} onDone={onClose} />}
            {stage === "declined" && <DeclinedStage onRestart={() => reset("proposed")} onClose={onClose} />}
            <Dialog.Close aria-label="Close" data-testid="connection-flow-close" className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/85 text-[#08090D] shadow-md transition-transform hover:scale-105">
              <X size={18} />
            </Dialog.Close>
          </Dialog.Content>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
};
